import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { resolvePhotoUrl } from '@/lib/photo';
import {
  Printer, Download, Share2, CheckCircle2, Clock, MapPin, CalendarDays,
  Loader2, Trophy, Ticket
} from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

// Phase 87 — Standalone, print-friendly Gate Pass slip.
// Renders outside AppShell (clean layout) at /events/:eventId/registrations/:regId/gatepass.
// Used by:
//   - Coaches at the gate (manual entry / arrived without phone)
//   - Students/Parents pre-event (downloadable from receipt dialog)
//   - Admins from the participants modal

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventGatePass() {
  const { eventId, regId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const slipRef = useRef(null);

  useEffect(() => {
    document.title = 'Event Gate Pass — Out-Play';
    api.get(`/events/${eventId}/registrations/${regId}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Could not load gate pass'));
  }, [eventId, regId]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="text-center max-w-sm" data-testid="gatepass-error">
        <Ticket className="h-10 w-10 text-muted-foreground/40 mx-auto mb-2" />
        <h1 className="text-lg font-display font-semibold">Pass unavailable</h1>
        <p className="text-sm text-muted-foreground mt-1">{error}</p>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
    </div>
  );

  const { registration: reg, event, academy, participant_photo_url } = data;
  const photoSrc = resolvePhotoUrl(participant_photo_url);
  const showPhoto = photoSrc && !photoFailed;
  const qrSrc = `${BACKEND_URL}/api/events/${eventId}/registrations/${regId}/qr?t=${localStorage.getItem('outplay_token') || ''}`;
  const isCheckedIn = !!reg.checked_in_at;
  const checkInTime = isCheckedIn ? new Date(reg.checked_in_at).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
  }) : null;

  const handlePrint = () => {
    setBusy(true);
    // Slight delay so React paints any state changes before print preview.
    setTimeout(() => { window.print(); setBusy(false); }, 100);
  };

  const handleDownloadPng = async () => {
    if (!slipRef.current) return;
    setBusy(true);
    try {
      // Wait for the QR <img> to fully decode — html2canvas will skip half-loaded images.
      const imgs = slipRef.current.querySelectorAll('img');
      await Promise.all(Array.from(imgs).map(img => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve; img.onerror = resolve;
          setTimeout(resolve, 2500); // hard timeout — never block forever
        });
      }));
      const canvas = await html2canvas(slipRef.current, {
        backgroundColor: '#ffffff', scale: 2, useCORS: true, logging: false,
      });
      const link = document.createElement('a');
      link.download = `GatePass-${reg.ticket_code || regId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Gate pass saved');
    } catch (e) {
      toast.error('Could not generate PNG. Try Print instead.');
    } finally {
      setBusy(false);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    const text = `Gate Pass: ${reg.participant_name} for ${event?.title || 'the event'} — ticket ${reg.ticket_code}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 py-4 px-3 print:bg-white print:py-0 print:px-0" data-testid="gatepass-page">
      {/* Phase 247.e — Toolbar pinned to the viewport top on small screens so
          Print / Save-PNG / Share never slip below the fold when the slip
          itself is taller than the viewport (e.g. landscape phone). */}
      <div
        className="sticky top-0 z-10 -mx-3 px-3 py-2 mb-3 bg-slate-100/95 dark:bg-slate-950/95 backdrop-blur supports-[backdrop-filter]:bg-slate-100/80 print:static print:bg-transparent print:py-0 print:mb-0 print:hidden"
        data-testid="gatepass-toolbar"
      >
        <div className="max-w-md mx-auto flex flex-wrap gap-2 justify-center">
          <Button size="sm" onClick={handlePrint} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="gatepass-print">
            <Printer className="h-3.5 w-3.5 mr-1" /> Print
          </Button>
          <Button size="sm" variant="outline" onClick={handleDownloadPng} disabled={busy} data-testid="gatepass-download">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />} Save as PNG
          </Button>
          <Button size="sm" variant="outline" onClick={handleShare} data-testid="gatepass-share">
            <Share2 className="h-3.5 w-3.5 mr-1" /> Share
          </Button>
        </div>
      </div>

      {/* The slip itself — captured by html2canvas */}
      <div
        ref={slipRef}
        className="max-w-md mx-auto bg-white rounded-2xl overflow-hidden shadow-xl print:shadow-none print:rounded-none"
        data-testid="gatepass-slip"
      >
        {/* Header: academy */}
        <div className="bg-gradient-to-br from-emerald-700 via-teal-700 to-emerald-900 text-white p-5 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
          <div className="relative flex items-center gap-3">
            {academy.logo_url ? (
              <img src={academy.logo_url} alt={academy.name} className="h-12 w-12 rounded-lg bg-white p-1 object-contain shadow" crossOrigin="anonymous" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <div className="h-12 w-12 rounded-lg bg-white/20 flex items-center justify-center font-bold">{(academy.name || 'A')[0]}</div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wider opacity-80 flex items-center gap-1"><Trophy className="h-3 w-3" /> Gate Pass</p>
              <p className="font-display font-bold text-base leading-tight truncate" data-testid="gatepass-academy-name">{academy.name || 'Academy'}</p>
              {(academy.city || academy.state) && <p className="text-[10px] opacity-80 truncate">{[academy.city, academy.state].filter(Boolean).join(', ')}</p>}
            </div>
          </div>
        </div>

        {/* Participant */}
        <div className="p-5 flex items-center gap-4 border-b">
          <div className="shrink-0">
            {showPhoto ? (
              <img src={photoSrc} alt={reg.participant_name} className="h-20 w-20 rounded-full object-cover border-4 border-emerald-100 shadow" crossOrigin="anonymous" onError={() => setPhotoFailed(true)} data-testid="gatepass-photo" />
            ) : (
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center text-2xl font-bold border-4 border-emerald-100 shadow" data-testid="gatepass-photo-placeholder">
                {(reg.participant_name || '?')[0].toUpperCase()}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Participant</p>
            <p className="font-display font-bold text-xl leading-tight truncate" data-testid="gatepass-participant-name">{reg.participant_name}</p>
            {reg.team_name && <p className="text-sm text-muted-foreground truncate">{reg.team_name}</p>}
            <div className="mt-1.5 flex flex-wrap gap-1">
              {reg.payment_status === 'paid' && (
                <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]" data-testid="gatepass-paid-badge">₹{reg.paid_amount || reg.entry_fee} Paid</Badge>
              )}
              {reg.payment_status === 'free' && (
                <Badge className="bg-slate-100 text-slate-700 border-0 text-[9px]">Free entry</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Event details */}
        <div className="px-5 py-4 border-b space-y-1.5 text-sm">
          <p className="font-semibold leading-tight" data-testid="gatepass-event-title">{event?.title}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CalendarDays className="h-3 w-3" />
            <span>{event?.date}{event?.end_date && event?.end_date !== event?.date ? ` — ${event.end_date}` : ''}</span>
          </div>
          {event?.venue && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              <span className="truncate">{event.venue}</span>
            </div>
          )}
        </div>

        {/* QR + ticket code */}
        <div className="p-5 flex items-center gap-4 border-b">
          <div className="rounded-lg border-2 border-dashed border-emerald-200 bg-emerald-50/30 p-2 shrink-0">
            <img src={qrSrc} alt="Ticket QR" className="h-28 w-28" crossOrigin="anonymous" data-testid="gatepass-qr" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Ticket Code</p>
            <p className="font-mono text-sm font-bold break-all leading-tight" data-testid="gatepass-ticket-code">{reg.ticket_code}</p>
            <p className="text-[10px] text-muted-foreground mt-1.5">Scan at the gate or enter manually.</p>
          </div>
        </div>

        {/* Check-in status */}
        <div className={`p-4 text-center ${isCheckedIn ? 'bg-emerald-50 dark:bg-emerald-950/30' : 'bg-amber-50 dark:bg-amber-950/30'}`} data-testid="gatepass-checkin-status">
          {isCheckedIn ? (
            <div className="flex items-center justify-center gap-2 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold">Checked In</p>
                <p className="text-sm font-semibold tabular-nums">{checkInTime}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-amber-700">
              <Clock className="h-5 w-5" />
              <p className="text-sm font-semibold">Awaiting check-in at the gate</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 text-center bg-slate-50 dark:bg-slate-900/40">
          <p className="text-[9px] text-muted-foreground">Powered by Out-Play.in · {reg.id?.slice(0, 8)}</p>
        </div>
      </div>
    </div>
  );
}
